// File: app/src/utils/benchmarkNormalise.js
//
// Turns a benchmark (a performance_capabilities row) into one comparable
// figure (issue #88):
//   - a loaded lift for reps, with how hard it was → estimated 1RM
//   - a time for a distance → pace per 500 m or per km, plus race equivalents
//   - calories against a time → calories per minute
// and dates it so a stale result can be flagged.
//
// Effort stays the coarse words people can judge (All out / Hard / Moderate):
// reps-in-reserve ratings are only reliable near failure, so the words carry
// about all the precision there is. They map to reps in reserve here.
//
// Pure: no React, no I/O.

import { showWeight } from "./units";
import { secondsToClock } from "./clockInput";

const positive = (raw) => {
  // pg returns NUMERIC as strings.
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
};

// Tolerant of how the user typed the marker: "1RM", "1 rm", "1rm" all qualify.
export const isOneRmMetric = (metric) =>
  String(metric || "")
    .replace(/\s+/g, "")
    .toLowerCase() === "1rm";

/** The reps in a "3RM" / "5 RM" style marker, or null (1RM included). */
export const rmMetricReps = (metric) => {
  const match = String(metric || "")
    .replace(/\s+/g, "")
    .toLowerCase()
    .match(/^(\d+)rm$/);
  return match ? Number(match[1]) : null;
};

// ─── Effort → reps in reserve ────────────────────────────

/**
 * Reps in reserve implied by an effort note, or null when it says nothing
 * usable. Reads the chips (All out / Hard / Moderate) and older free text
 * such as "RPE 8", "RIR 2" or "2 left".
 */
export const effortToRir = (effort) => {
  const text = String(effort || "").toLowerCase();
  if (!text.trim()) return null;
  const rpe = text.match(/rpe\s*(\d+(?:\.\d+)?)/);
  if (rpe) {
    const n = Number(rpe[1]);
    return n >= 5 && n <= 10 ? 10 - n : null;
  }
  const rir = text.match(
    /rir\s*(\d+(?:\.\d+)?)|(\d+(?:\.\d+)?)\+?\s*(?:reps?\s+)?(?:left|in reserve|in the tank)/,
  );
  if (rir) return Number(rir[1] ?? rir[2]);
  if (/all[\s-]?out|max(imal)?\b|failure|nothing left/.test(text)) return 0;
  // The timed-effort options (benchmarkForm) read like the older words.
  if (/hard|a bit left/.test(text)) return 1.5;
  if (/moderate|medium|held back/.test(text)) return 3.5;
  return null;
};

// ─── Estimated 1RM ───────────────────────────────────────

// %1RM by reps to failure (reps done + reps in reserve), 1–12, from the
// RTS/Tuchscherer RPE chart's RPE 10 row. Each RPE step down is one more rep
// in reserve, so this one row covers the whole chart.
const RTS_PCT = [100, 95.5, 92.2, 89.2, 86.3, 83.7, 81.1, 78.6, 76.2, 73.9, 70.7, 68.0];
const MAX_TABLE_REPS = RTS_PCT.length;
// Past ~20 reps to failure a set says more about endurance than strength.
export const MAX_ESTIMATE_REPS = 20;

const pctAt = (repsToFailure) => {
  if (repsToFailure <= MAX_TABLE_REPS) {
    const lo = Math.floor(repsToFailure);
    const hi = Math.ceil(repsToFailure);
    const frac = repsToFailure - lo;
    return RTS_PCT[lo - 1] + (RTS_PCT[hi - 1] - RTS_PCT[lo - 1]) * frac;
  }
  // Beyond the chart, follow Epley's slope from the chart's last point so the
  // curve stays continuous.
  const epley = (n) => 1 + n / 30;
  return (RTS_PCT[MAX_TABLE_REPS - 1] * epley(MAX_TABLE_REPS)) / epley(repsToFailure);
};

/**
 * The fraction of 1RM that can be lifted for this many reps to failure. `k`
 * is a personal factor (fitRepCurve): it scales how much is lost per extra
 * rep, so 1 is the chart, below 1 holds on for more reps, above 1 fewer.
 */
export const repPercent = (repsToFailure, k = 1) => 1 - k * (1 - pctAt(repsToFailure) / 100);

/** Load, reps and reps to failure for a loaded lift row, or null. */
export const liftSet = (cap) => {
  if (!cap || cap.unit !== "kg") return null;
  const load = positive(cap.value);
  if (load == null) return null;
  // Reps from the target, else from a "1RM" / "3 RM" marker.
  const reps =
    cap.target_unit === "reps"
      ? positive(cap.target_value)
      : isOneRmMetric(cap.metric)
        ? 1
        : rmMetricReps(cap.metric);
  if (reps == null || reps !== Math.round(reps)) return null;
  const statedRir = effortToRir(cap.effort);
  const rir = statedRir ?? 0;
  return { load, reps, statedRir, rir, repsToFailure: reps + rir };
};

/**
 * Estimated 1RM in kg for a lift, or null when the row isn't a loaded lift
 * with known reps. `rough` marks an estimate from far off failure (3+ in
 * reserve), from a long set, or with no effort given (then it assumes all
 * out, so it errs low). `tested` is true for a straight 1RM that needs no
 * estimate.
 */
export const estimateOneRm = (cap, k = 1) => {
  const set = liftSet(cap);
  if (!set) return null;
  const { load, statedRir, rir, repsToFailure } = set;
  if (repsToFailure > MAX_ESTIMATE_REPS) return null;
  if (repsToFailure === 1) return { kg: load, rough: false, tested: true };
  return {
    kg: Math.round(load / repPercent(repsToFailure, k)),
    rough: statedRir == null || rir >= 3 || repsToFailure > MAX_TABLE_REPS,
    tested: false,
  };
};

// ─── Endurance ───────────────────────────────────────────

// Rowers and ski ergs read pace per 500 m; everything else per km.
export const isErg = (label) => {
  const text = String(label || "").toLowerCase();
  return !/bike/.test(text) && /\brow|rower|\bski|\berg\b|concept\s*2|\bc2\b/.test(text);
};

/** Seconds and metres for a timed distance, either way round. */
export const timeForDistance = (cap) => {
  if (cap.unit === "sec" && cap.target_unit === "m") {
    return { sec: positive(cap.value), m: positive(cap.target_value) };
  }
  if (cap.unit === "m" && cap.target_unit === "sec") {
    return { sec: positive(cap.target_value), m: positive(cap.value) };
  }
  return null;
};

/** Seconds and calories, either way round. */
export const timeForCalories = (cap) => {
  if (cap.unit === "sec" && cap.target_unit === "cals") {
    return { sec: positive(cap.value), cals: positive(cap.target_value) };
  }
  if (cap.unit === "cals" && cap.target_unit === "sec") {
    return { sec: positive(cap.target_value), cals: positive(cap.value) };
  }
  return null;
};

/** Riegel: T2 = T1 × (D2 / D1)^1.06. */
export const riegelTime = (sec, metres, toMetres) => sec * (toMetres / metres) ** 1.06;

const ERG_LADDER = [1000, 2000, 5000, 6000, 10000];
const RUN_LADDER = [1000, 3000, 5000, 10000, 21097.5];

export const formatDistance = (m) => {
  if (m === 21097.5) return "half marathon";
  return m >= 1000 && m % 1000 === 0 ? `${m / 1000}K` : `${m} m`;
};

// Riegel holds for efforts of a few minutes to a few hours, and gets worse
// the further it projects, so only nearby standard distances are offered.
const equivalents = (sec, metres, erg) => {
  const ladder = erg ? ERG_LADDER : RUN_LADDER;
  return ladder
    .filter((d) => Math.abs(d - metres) / metres > 0.05 && d / metres >= 0.4 && d / metres <= 2.5)
    .map((d) => ({ metres: d, sec: riegelTime(sec, metres, d) }))
    .filter((e) => e.sec >= 150)
    .sort((a, b) => Math.abs(Math.log(a.metres / metres)) - Math.abs(Math.log(b.metres / metres)))
    .slice(0, 2)
    .sort((a, b) => a.metres - b.metres);
};

export const clock = (sec) => {
  const total = Math.round(sec);
  if (total < 3600) return secondsToClock(total);
  const h = Math.floor(total / 3600);
  return `${h}:${secondsToClock(total - h * 3600).padStart(5, "0")}`;
};

/**
 * Pace and race equivalents for a timed distance, or calories per minute for
 * a calorie test. Null for anything else.
 */
export const normaliseEndurance = (cap) => {
  if (!cap) return null;
  const dist = timeForDistance(cap);
  if (dist && dist.sec != null && dist.m != null) {
    const erg = isErg(cap.label);
    const per = erg ? 500 : 1000;
    return {
      kind: "pace",
      pace: clock((dist.sec / dist.m) * per),
      per: erg ? "/500 m" : "/km",
      equivalents: equivalents(dist.sec, dist.m, erg).map((e) => ({
        distance: formatDistance(e.metres),
        time: clock(e.sec),
      })),
    };
  }
  const cals = timeForCalories(cap);
  if (cals && cals.sec != null && cals.cals != null) {
    return { kind: "rate", calPerMin: Math.round((cals.cals / cals.sec) * 60 * 10) / 10 };
  }
  return null;
};

// ─── Combined figure and age ─────────────────────────────

/**
 * The normalised figure for display, e.g. "≈ 115 kg 1RM", "1:53 /500 m" or
 * "≈ 18 cal/min", or null when there's nothing to add to the raw result.
 */
export const normalisedFigure = (cap) => {
  const oneRm = estimateOneRm(cap);
  if (oneRm) return oneRm.tested ? null : `≈ ${showWeight(oneRm.kg)} 1RM`;
  const endurance = normaliseEndurance(cap);
  if (endurance?.kind === "pace") return `${endurance.pace} ${endurance.per}`;
  if (endurance?.kind === "rate") return `≈ ${endurance.calPerMin} cal/min`;
  return null;
};

export const STALE_DAYS = 183;

// YYYY-MM-DD as a local date, so the day can't shift with the timezone.
const parseDay = (raw) => {
  if (!raw) return null;
  const match = String(raw).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const d = match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * When a result dates from: its test date, or for older entries with none,
 * the day it was entered (the test was on or before that). `stale` once it's
 * about six months old.
 */
export const benchmarkAge = (cap, now = new Date()) => {
  const date = parseDay(cap?.tested_on) || parseDay(cap?.created_at);
  if (!date) return null;
  const days = Math.floor((now.getTime() - date.getTime()) / 86400000);
  return { date, days, estimated: !cap.tested_on, stale: days >= STALE_DAYS };
};

// ─── Combined 1RM ────────────────────────────────────────

// An estimate counts for less the older it is (about a third at four
// months) and the further it came from failure (issue #91).
const RECENCY_DAYS = 120;
const firmness = (est) => (est.tested ? 1 : est.rough ? 0.4 : 0.8);
// Fresh estimates further apart than this are flagged as disagreeing.
const CONFLICT_RATIO = 1.1;

// The personal factor lifters actually show. Past these, the rows more
// likely disagree than describe the lifter.
const K_MIN = 0.6;
const K_MAX = 1.6;
// Sets this close to a max lose too little to measure how reps fall off.
const MIN_FIT_REPS = 3;

/**
 * How this lifter's reps fall off with load, from their own results on one
 * lift: the most recent tested 1RM from the last six months, against firm
 * sets (All out or Hard, 3 to 12 reps to failure) from the same period.
 * Returns { k, anchor, sets } where `sets` is how many shaped it, or null
 * when there's no recent tested max or no such set, and the chart stands.
 */
export const fitRepCurve = (rows, now = new Date()) => {
  const fresh = (rows || []).filter((cap) => !benchmarkAge(cap, now)?.stale);
  const tested = fresh
    .filter((cap) => liftSet(cap)?.repsToFailure === 1)
    .sort((a, b) => (benchmarkAge(a, now)?.days ?? 0) - (benchmarkAge(b, now)?.days ?? 0))[0];
  if (!tested) return null;
  const anchor = liftSet(tested).load;
  let num = 0;
  let den = 0;
  let sets = 0;
  for (const cap of fresh) {
    const set = liftSet(cap);
    const est = estimateOneRm(cap);
    if (!set || !est || est.tested || est.rough || set.repsToFailure < MIN_FIT_REPS) continue;
    // Least squares on the drop from the max: seen = k × chart.
    const seen = 1 - set.load / anchor;
    const chart = 1 - repPercent(set.repsToFailure);
    num += seen * chart;
    den += chart * chart;
    sets += 1;
  }
  const k = sets ? num / den : null;
  // Outside the believable range the rows contradict each other (e.g. a max
  // equalled for five reps); keep the chart and let the conflict show.
  if (k == null || k < K_MIN || k > K_MAX) return null;
  return { k, anchor, sets };
};

/**
 * One estimated 1RM from several rows for the same lift, or null when none
 * gives an estimate. Rows from the last six months are used when there are
 * any; older ones only stand in when there's nothing newer (`stale`).
 * Estimates follow the lifter's own rep curve when fitRepCurve has one
 * (`curve`), so a lifter who holds reps well isn't credited with a max they
 * don't have. `conflict` gives the range when the rows used disagree by
 * more than 10%; `entries` lists each row's estimate and share.
 */
export const combineOneRm = (rows, now = new Date()) => {
  const curve = fitRepCurve(rows, now);
  const k = curve?.k ?? 1;
  const all = (rows || [])
    .map((cap) => {
      const est = estimateOneRm(cap, k);
      if (!est) return null;
      const age = benchmarkAge(cap, now);
      return { ...est, cap, days: Math.max(0, age?.days ?? 0), stale: !!age?.stale };
    })
    .filter(Boolean);
  if (all.length === 0) return null;
  const fresh = all.filter((e) => !e.stale);
  const used = fresh.length ? fresh : all;

  let weighted = 0;
  let total = 0;
  const weights = new Map();
  for (const e of used) {
    const w = Math.exp(-e.days / RECENCY_DAYS) * firmness(e);
    weights.set(e, w);
    weighted += w * e.kg;
    total += w;
  }
  const kgs = used.map((e) => e.kg);
  const low = Math.min(...kgs);
  const high = Math.max(...kgs);

  return {
    kg: Math.round(weighted / total),
    rows: used.length,
    rough: used.every((e) => e.rough),
    stale: !fresh.length,
    curve,
    conflict: used.length > 1 && high / low > CONFLICT_RATIO ? { low, high } : null,
    // Each row's estimate and its share of the result (0 when not used).
    entries: all.map(({ cap, kg, tested, rough, stale }, i) => ({
      cap,
      kg,
      tested,
      rough,
      stale,
      share: (weights.get(all[i]) ?? 0) / total,
    })),
  };
};

/** Today as YYYY-MM-DD in local time, for a date input's default. */
export const todayIso = (now = new Date()) =>
  `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
